import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { getProjects } from '../Redux/Actions/ProyectActioins';

const FILTERS = ['Todos', 'web', 'mobile', 'iot', 'fullstack', 'ia'];

const filterLabel = (f) => {
  const map = { Todos: 'Todos', web: 'Web', mobile: 'Mobile', iot: 'IoT', fullstack: 'Full Stack', ia: 'IA' };
  return map[f] || f;
};

// Parsea tipos multi-valor "web,iot" → ['web','iot']
const parseTypes = (raw) => {
  if (!raw) return [];
  return String(raw).split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
};

/* ─── Image Carousel ─────────────────────────────────────────────── */
const ImageCarousel = ({ images, title }) => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!images || images.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % images.length), 4000);
    return () => clearInterval(t);
  }, [images]);

  if (!images || images.length === 0) return (
    <div style={{ height: '200px', background: 'rgba(255,69,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '12px 12px 0 0' }}>
      <span style={{ fontSize: '2.5rem', opacity: 0.3 }}>🖥️</span>
    </div>
  );

  return (
    <div style={{ position: 'relative', height: '200px', overflow: 'hidden', borderRadius: '12px 12px 0 0' }}>
      <AnimatePresence mode="wait">
        <motion.img
          key={idx}
          src={images[idx]}
          alt={title}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </AnimatePresence>
      {images.length > 1 && (
        <div style={{ position: 'absolute', bottom: '8px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '4px' }}>
          {images.map((_, i) => (
            <div key={i} style={{ width: '5px', height: '5px', borderRadius: '50%', background: i === idx ? '#ff4500' : 'rgba(255,255,255,0.4)', transition: 'background 0.3s' }} />
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── Project Modal ──────────────────────────────────────────────── */
const ProjectModal = ({ project, onClose }) => {
  const [imgIdx, setImgIdx] = useState(0);
  const images = project.images || [];

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const types = parseTypes(project.type);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
        backdropFilter: 'blur(8px)', zIndex: 2000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.92, y: 20 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#141414', border: '1px solid rgba(255,69,0,0.2)',
          borderRadius: '20px', maxWidth: '760px', width: '100%',
          maxHeight: '90vh', overflow: 'auto',
        }}
      >
        {images.length > 0 && (
          <div style={{ position: 'relative' }}>
            <AnimatePresence mode="wait">
              <motion.img
                key={imgIdx}
                src={images[imgIdx]}
                alt={project.title}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{ width: '100%', height: '320px', objectFit: 'cover', borderRadius: '20px 20px 0 0', display: 'block' }}
              />
            </AnimatePresence>
            {images.length > 1 && (
              <>
                <button onClick={() => setImgIdx((i) => (i - 1 + images.length) % images.length)}
                  style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', width: '36px', height: '36px', borderRadius: '50%', cursor: 'pointer', fontSize: '1rem' }}>
                  ‹
                </button>
                <button onClick={() => setImgIdx((i) => (i + 1) % images.length)}
                  style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', width: '36px', height: '36px', borderRadius: '50%', cursor: 'pointer', fontSize: '1rem' }}>
                  ›
                </button>
                <div style={{ position: 'absolute', bottom: '10px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '5px' }}>
                  {images.map((_, i) => (
                    <button key={i} onClick={() => setImgIdx(i)} style={{ width: '6px', height: '6px', borderRadius: '50%', border: 'none', background: i === imgIdx ? '#ff4500' : 'rgba(255,255,255,0.4)', cursor: 'pointer', padding: 0 }} />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <div style={{ padding: '2rem' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem', gap: '1rem' }}>
            <div>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                {types.length > 0
                  ? types.map(t => (
                    <span key={t} style={{ display: 'inline-block', padding: '0.2rem 0.7rem', borderRadius: '20px', background: 'rgba(255,69,0,0.12)', border: '1px solid rgba(255,69,0,0.25)', color: '#ff7b47', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                      {t}
                    </span>
                  ))
                  : <span style={{ display: 'inline-block', padding: '0.2rem 0.7rem', borderRadius: '20px', background: 'rgba(255,69,0,0.12)', border: '1px solid rgba(255,69,0,0.25)', color: '#ff7b47', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Proyecto</span>
                }
              </div>
              <h2 style={{ color: '#f2f2f2', fontWeight: 800, fontSize: '1.5rem' }}>{project.title}</h2>
            </div>
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(242,242,242,0.6)', width: '36px', height: '36px', borderRadius: '50%', cursor: 'pointer', fontSize: '1rem', flexShrink: 0 }}>
              ✕
            </button>
          </div>

          <p style={{ color: 'rgba(242,242,242,0.65)', lineHeight: 1.8, marginBottom: '1.5rem', fontSize: '0.9rem', whiteSpace: 'pre-line' }}>
            {project.description}
          </p>

          <div style={{ borderTop: '1px solid rgba(255,69,0,0.15)', paddingTop: '1.25rem', marginBottom: '1.5rem' }}>
            <p style={{ color: 'rgba(242,242,242,0.35)', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.75rem' }}>Tecnologías</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {(project.technologies || '').split(',').map((t) => t.trim()).filter(Boolean).map((tech) => (
                <span key={tech} style={{ padding: '0.25rem 0.6rem', borderRadius: '20px', fontSize: '0.75rem', background: 'rgba(255,69,0,0.08)', border: '1px solid rgba(255,69,0,0.2)', color: '#ff7b47' }}>
                  {tech}
                </span>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {project.deployUrl && (
              <a href={project.deployUrl} target="_blank" rel="noopener noreferrer"
                style={{ padding: '0.7rem 1.5rem', background: 'linear-gradient(135deg, #ff4500, #ff7b47)', color: 'white', borderRadius: '8px', textDecoration: 'none', fontWeight: 700, fontSize: '0.85rem' }}>
                Ver proyecto →
              </a>
            )}
            {project.repositoryUrl && (
              <a href={project.repositoryUrl} target="_blank" rel="noopener noreferrer"
                style={{ padding: '0.7rem 1.5rem', background: 'transparent', border: '1px solid rgba(255,69,0,0.3)', color: '#ff7b47', borderRadius: '8px', textDecoration: 'none', fontWeight: 600, fontSize: '0.85rem' }}>
                Ver código
              </a>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ─── Project Card ───────────────────────────────────────────────── */
const ProjectCard = ({ project, onClick, index, featured }) => {
  const types = parseTypes(project.type);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.4, delay: (index % 4) * 0.07 }}
      whileHover={{ y: -6 }}
      onClick={onClick}
      style={{
        background: featured ? 'rgba(255,215,0,0.03)' : 'rgba(255,255,255,0.02)',
        border: featured ? '1px solid rgba(255,215,0,0.2)' : '1px solid rgba(255,255,255,0.07)',
        borderRadius: '16px', overflow: 'hidden', cursor: 'pointer', position: 'relative',
        boxShadow: featured ? '0 0 20px rgba(255,215,0,0.05)' : 'none',
        transition: 'border-color 0.3s, box-shadow 0.3s',
      }}
    >
      {featured && (
        <div style={{ position: 'absolute', top: '10px', left: '10px', zIndex: 2, background: 'rgba(255,215,0,0.15)', border: '1px solid rgba(255,215,0,0.4)', color: '#ffd700', fontSize: '0.63rem', fontWeight: 800, padding: '2px 9px', borderRadius: '20px', letterSpacing: '0.8px', textTransform: 'uppercase', backdropFilter: 'blur(4px)' }}>
          ★ Destacado
        </div>
      )}

      <ImageCarousel images={project.images} title={project.title} />

      <div style={{ padding: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem', gap: '0.5rem' }}>
          <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
            {types.length > 0
              ? types.map(t => (
                <span key={t} style={{ padding: '0.18rem 0.55rem', borderRadius: '20px', fontSize: '0.65rem', background: 'rgba(255,69,0,0.1)', border: '1px solid rgba(255,69,0,0.2)', color: '#ff7b47', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  {t}
                </span>
              ))
              : <span style={{ padding: '0.18rem 0.55rem', borderRadius: '20px', fontSize: '0.65rem', background: 'rgba(255,69,0,0.1)', border: '1px solid rgba(255,69,0,0.2)', color: '#ff7b47', textTransform: 'uppercase', letterSpacing: '1px' }}>Proyecto</span>
            }
          </div>
          <span style={{ color: 'rgba(242,242,242,0.25)', fontSize: '0.72rem', flexShrink: 0 }}>Ver detalles →</span>
        </div>

        <h3 style={{ color: '#f2f2f2', fontWeight: 700, fontSize: '1rem', marginBottom: '0.5rem', lineHeight: 1.3 }}>
          {project.title}
        </h3>

        <p style={{ color: 'rgba(242,242,242,0.5)', fontSize: '0.8rem', lineHeight: 1.6, marginBottom: '0.85rem',
          overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {project.description}
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
          {(project.technologies || '').split(',').slice(0, 4).map((t) => t.trim()).filter(Boolean).map((tech) => (
            <span key={tech} style={{ padding: '0.15rem 0.45rem', borderRadius: '20px', fontSize: '0.68rem', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(242,242,242,0.5)' }}>
              {tech}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

/* ─── Main ───────────────────────────────────────────────────────── */
const Projects = () => {
  const dispatch = useDispatch();
  const { projects: projectList, loading } = useSelector((state) => state.projects);
  const projects = projectList || []; // si la API no responde, no rompe la página
  const [activeFilter, setActiveFilter] = useState('Todos');
  const [selectedProject, setSelectedProject] = useState(null);
  const [showRest, setShowRest] = useState(false);

  useEffect(() => {
    dispatch(getProjects());
  }, [dispatch]);

  // Filtro multi-tipo compatible
  const filtered = activeFilter === 'Todos'
    ? projects
    : projects.filter((p) => parseTypes(p.type).includes(activeFilter.toLowerCase()));

  const availableFilters = FILTERS.filter(
    (f) => f === 'Todos' || projects.some((p) => parseTypes(p.type).includes(f.toLowerCase()))
  );

  const featured = filtered.filter((p) => p.featured);
  const rest     = filtered.filter((p) => !p.featured);
  const hasFeatured = featured.length > 0;

  // Al cambiar filtro, resetear el panel expandido
  useEffect(() => { setShowRest(false); }, [activeFilter]);

  return (
    <>
      <section id="projects" style={{ background: '#0a0a0a', padding: '6rem 0', position: 'relative' }}>
        {/* Grid sutil de fondo */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none',
          backgroundImage: 'linear-gradient(rgba(255,69,0,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,69,0,0.03) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />

        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem', position: 'relative', zIndex: 1 }}>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginBottom: '3.5rem' }}
          >
            <p style={{ color: '#ff4500', fontSize: '0.8rem', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
              Lo que construí
            </p>
            <h2 style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', color: '#f2f2f2', fontWeight: 800, marginBottom: '1rem' }}>
              Proyectos<span style={{ color: '#ff4500' }}> reales</span>,<br />
              resultados concretos
            </h2>
            <p style={{ color: 'rgba(242,242,242,0.45)', maxWidth: '500px', margin: '0 auto', lineHeight: 1.8, fontSize: '0.92rem' }}>
              Cada proyecto resolvió un problema real. Hacé click en cualquier tarjeta para ver los detalles completos.
            </p>
          </motion.div>

          {/* Filtros */}
          {availableFilters.length > 1 && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '3rem' }}
            >
              {availableFilters.map((f) => (
                <button
                  key={f}
                  onClick={() => setActiveFilter(f)}
                  style={{
                    padding: '0.45rem 1.1rem', borderRadius: '24px',
                    background: activeFilter === f ? 'rgba(255,69,0,0.15)' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${activeFilter === f ? 'rgba(255,69,0,0.5)' : 'rgba(255,255,255,0.07)'}`,
                    color: activeFilter === f ? '#ff7b47' : 'rgba(242,242,242,0.45)',
                    fontSize: '0.82rem', fontWeight: activeFilter === f ? 700 : 500,
                    cursor: 'pointer', transition: 'all 0.2s',
                  }}
                >
                  {filterLabel(f)}
                </button>
              ))}
            </motion.div>
          )}

          {/* ── Contenido ── */}
          {loading ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} style={{ height: '340px', borderRadius: '16px', background: 'rgba(255,255,255,0.03)' }} />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '5rem 0', color: 'rgba(242,242,242,0.3)' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
              <p>No hay proyectos en esta categoría todavía.</p>
            </div>
          ) : hasFeatured ? (
            <>
              {/* ── SECCIÓN DESTACADOS ── */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                style={{ marginBottom: '2rem' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                  <span style={{ color: '#ffd700', fontSize: '1rem' }}>★</span>
                  <h3 style={{ color: '#f2f2f2', fontWeight: 800, fontSize: '1.05rem', margin: 0 }}>Proyectos destacados</h3>
                  <div style={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, rgba(255,215,0,0.2), transparent)' }} />
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={`featured-${activeFilter}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}
                  >
                    {featured.map((project, i) => (
                      <ProjectCard
                        key={project._id}
                        project={project}
                        index={i}
                        featured={true}
                        onClick={() => setSelectedProject(project)}
                      />
                    ))}
                  </motion.div>
                </AnimatePresence>
              </motion.div>

              {/* ── SECCIÓN RESTO ── */}
              {rest.length > 0 && (
                <div>
                  {/* Botón expandir/colapsar */}
                  <motion.button
                    onClick={() => setShowRest(p => !p)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.75rem',
                      width: '100%', padding: '1rem 1.5rem', marginBottom: showRest ? '1.5rem' : 0,
                      background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)',
                      borderRadius: '14px', cursor: 'pointer', color: 'rgba(242,242,242,0.55)',
                      fontSize: '0.9rem', fontWeight: 600, transition: 'all 0.2s',
                    }}
                  >
                    <span style={{ fontSize: '1rem', transition: 'transform 0.3s', display: 'inline-block', transform: showRest ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
                    <span>{showRest ? 'Ocultar' : `Ver todos los proyectos`}</span>
                    <span style={{ marginLeft: '0.25rem', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(242,242,242,0.4)', fontSize: '0.75rem', fontWeight: 700, padding: '0.1rem 0.6rem', borderRadius: '20px' }}>
                      {rest.length}
                    </span>
                    <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.05)' }} />
                  </motion.button>

                  <AnimatePresence>
                    {showRest && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: 'easeInOut' }}
                        style={{ overflow: 'hidden' }}
                      >
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem', paddingTop: '0.25rem' }}>
                          {rest.map((project, i) => (
                            <ProjectCard
                              key={project._id}
                              project={project}
                              index={i}
                              featured={false}
                              onClick={() => setSelectedProject(project)}
                            />
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </>
          ) : (
            /* Sin destacados: mostrar todo normalmente */
            <AnimatePresence mode="wait">
              <motion.div
                key={activeFilter}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}
              >
                {filtered.map((project, i) => (
                  <ProjectCard
                    key={project._id}
                    project={project}
                    index={i}
                    featured={false}
                    onClick={() => setSelectedProject(project)}
                  />
                ))}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </section>

      {/* Modal */}
      <AnimatePresence>
        {selectedProject && (
          <ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} />
        )}
      </AnimatePresence>
    </>
  );
};

export default Projects;
